// src/components/MyFlights.jsx
import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { santaUrl } from "../utils/santaApi";

function MyFlights({ onFlightSelect }) {
  /* ===============================
     FLIGHT STATE
  =============================== */
  const [flightCode, setFlightCode] = useState("");
  const [flightDate, setFlightDate] = useState("");
  const [lookupError, setLookupError] = useState("");

  /* ===============================
     SANTA STATE
     parked    -> hidden surprise: no button, sleigh sits at the North Pole
     countdown / active / done -> December UI
  =============================== */
  const [santaState, setSantaState] = useState("parked");
  const [santaLaunchAt, setSantaLaunchAt] = useState(null);
  const [santaCountdown, setSantaCountdown] = useState(null);
  const santaTimerRef = useRef(null);

  /* ===============================
     FETCH SANTA STATE
  =============================== */
  const loadSantaState = useCallback(() => {
    fetch(santaUrl("state"))
      .then((res) => res.json())
      .then((data) => {
        setSantaState(data.state || "parked");
        setSantaLaunchAt(data.launchAt || null);
      })
      .catch(() => setSantaState("parked"));
  }, []);

  useEffect(() => {
    loadSantaState();
  }, [loadSantaState]);

  /* ===============================
     CLEANUP ON UNMOUNT
  =============================== */
  useEffect(() => {
    return () => {
      if (santaTimerRef.current) {
        clearInterval(santaTimerRef.current);
        santaTimerRef.current = null;
      }
    };
  }, []);

  /* ===============================
     COUNTDOWN (target comes from the server so there is one source of truth)
  =============================== */
  useEffect(() => {
    if (santaState !== "countdown" || !santaLaunchAt) {
      setSantaCountdown(null);
      return;
    }

    const launch = new Date(santaLaunchAt).getTime();

    const update = () => {
      const diffMs = launch - Date.now();

      if (diffMs <= 0) {
        // Launch time reached: ask the server for the new state (active)
        setSantaCountdown(null);
        loadSantaState();
        return false;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      setSantaCountdown({
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      });
      return true;
    };

    if (!update()) return;

    const timer = setInterval(() => {
      if (!update()) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [santaState, santaLaunchAt, loadSantaState]);

  /* ===============================
     TRACK SANTA (POLLING)
  =============================== */
  const handleTrackSanta = async () => {
    setLookupError("");
    // Clear existing poller if any
    if (santaTimerRef.current) {
      clearInterval(santaTimerRef.current);
      santaTimerRef.current = null;
    }

    const fetchSanta = async () => {
      try {
        const res = await fetch(santaUrl("position"));
        const data = await res.json();

        if (data.status === "active" && onFlightSelect) {
          onFlightSelect(data);
        }

        if (data.status === "done") {
          clearInterval(santaTimerRef.current);
          santaTimerRef.current = null;
          setSantaState("done");
        }
      } catch (err) {
        console.error("Santa tracking error", err);
      }
    };

    // Fetch immediately
    fetchSanta();

    // Then poll every 30s
    santaTimerRef.current = setInterval(fetchSanta, 30_000);
  };

  /* ===============================
     FLIGHT LOGIC
  =============================== */
  const fetchFlightDetails = async (code, dateLocal) => {
    if (!code || !dateLocal) return;

    // Stop Santa tracking if active
    if (santaTimerRef.current) {
      clearInterval(santaTimerRef.current);
      santaTimerRef.current = null;
    }

    setLookupError("");

    try {
      const normalized = code.trim().toUpperCase();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/flightinfo`,
        { params: { code: normalized, date: dateLocal } }
      );

      if (onFlightSelect) onFlightSelect(res.data);
    } catch (err) {
      console.error("Failed to get flight info", err);
      setLookupError(
        `No flight data found for ${code.trim().toUpperCase()} on ${dateLocal}. ` +
          "Check the code and date. Only flights from about yesterday to tomorrow can be looked up."
      );
      if (onFlightSelect) onFlightSelect(null);
    }
  };

  const handleTrack = () => {
    if (!flightCode || !flightDate) return;
    fetchFlightDetails(flightCode, flightDate);
    setFlightCode("");
  };

  /* ===============================
     UI
  =============================== */
  const showSanta =
    santaState === "countdown" ||
    santaState === "active" ||
    santaState === "done";

  return (
    <div className="fd-card">
      <div className="fd-card-header">
        <div className="fd-card-title">MY FLIGHTS</div>
      </div>

      <div className="fd-input-row-modern">
        <div className="fd-input-group">
          <label>Flight Code</label>
          <input
            className="fd-input-modern"
            placeholder="e.g. UA123"
            value={flightCode}
            onChange={(e) => {
              setFlightCode(e.target.value);
              if (lookupError) setLookupError("");
            }}
          />
        </div>

        <div className="fd-input-group">
          <label>Date</label>
          <input
            type="date"
            className="fd-input-modern"
            value={flightDate}
            onChange={(e) => setFlightDate(e.target.value)}
          />
        </div>

        <button className="fd-track-modern" onClick={handleTrack}>
          Track
        </button>
      </div>

      {lookupError && (
        <p className="fd-muted" role="alert" style={{ marginTop: "8px", color: "#ff8a8a" }}>
          {lookupError}
        </p>
      )}

      {showSanta && (
        <div style={{ marginTop: "12px" }}>
          <button
            className="fd-track-modern"
            style={{ width: "100%", opacity: santaState === "active" ? 1 : 0.6 }}
            disabled={santaState !== "active"}
            onClick={handleTrackSanta}
          >
            🎅 Track Santa
          </button>

          {santaState === "countdown" && santaCountdown && (
            <p className="fd-muted" style={{ marginTop: "6px" }}>
              🎄 Santa departs in{" "}
              <strong>
                {santaCountdown.days}d {santaCountdown.hours}h{" "}
                {santaCountdown.minutes}m {santaCountdown.seconds}s
              </strong>
            </p>
          )}

          {santaState === "active" && (
            <p className="fd-muted" style={{ marginTop: "6px" }}>
              🎁 Santa is currently delivering gifts!
            </p>
          )}

          {santaState === "done" && (
            <p className="fd-muted" style={{ marginTop: "6px" }}>
              🎅 Santa has completed deliveries for this year
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default MyFlights;
